// A list's header, one for every list (the display decisions of 2026-10-29, points 9 A and 10 B): the icon, the title,
// and at the right end of the title's line the list's own controls (a view switch), ending at the rows' right edge; the
// list's ··· stays in the bar. Under the title, starting where the title's text starts, the quiet lines a list has to say
// (Today's date and birthdays, Habits' date, the Inbox's figures, a project's notes, who it is shared with), part of the
// header, so the gap to the first row is the same on every list. Lists with nothing to say have no line.
//
// The title stays on one line, never broken or hyphenated (Bastian, 2026-10-29: "Gewohnhei-/ten" beside Today | All).
// When the title and the controls don't fit on one line, the controls move to a row of their own under the title,
// starting at the title's text, before the lines under it. Decided by the real widths (any language, any name), measured
// whenever the header, the title or the controls change size.

import { useLayoutEffect, useRef, useState, type ReactNode } from "react";

export function ListHeader({ icon, title, detail, controls, children, className = "" }: {
  icon: ReactNode; title: ReactNode;
  /** Words that belong to the title, after it on its baseline, smaller (Today's short date); measured with it. */
  detail?: string | undefined;
  controls?: ReactNode; children?: ReactNode; className?: string;
}) {
  const line = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const after = useRef<HTMLSpanElement>(null);
  const tools = useRef<HTMLDivElement>(null);
  const [stacked, setStacked] = useState(false);
  const hasControls = Boolean(controls);

  useLayoutEffect(() => {
    const box = line.current;
    const text = heading.current;
    const own = tools.current;
    if (!box || !text || !own) {
      setStacked(false);
      return;
    }
    const measure = () => {
      // The title's whole width (scrollWidth, also while it is cut), the gap, the controls with their margins. The
      // controls' own margin-left (auto beside the title) doesn't count.
      const gap = parseFloat(getComputedStyle(box).columnGap) || 0;
      const words = after.current;
      const named = text.scrollWidth + (words ? words.offsetWidth + (parseFloat(getComputedStyle(words).marginLeft) || 0) : 0);
      const needed = named + gap + own.offsetWidth + (parseFloat(getComputedStyle(own).marginRight) || 0);
      setStacked(needed > box.clientWidth + 0.5);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(box);
    observer.observe(text);
    observer.observe(own);
    void document.fonts?.ready.then(measure);
    return () => observer.disconnect();
  }, [hasControls, title, detail]);

  return (
    <div className={`list-heading${className ? ` ${className}` : ""}`}>
      {icon}
      <div className="heading-text">
        <div ref={line} className={`heading-title${stacked ? " stacked" : ""}`}>
          <div className="heading-name">
            <h1 ref={heading} className="screen-title" title={typeof title === "string" ? title : undefined}>{title}</h1>
            {detail && <span ref={after} className="title-detail">{detail}</span>}
          </div>
          {controls && <div ref={tools} className="list-controls">{controls}</div>}
        </div>
        {children}
      </div>
    </div>
  );
}
